import { useState } from 'react';
import {
  CalendarX,
  Download,
  Upload,
  Plus,
  Play,
  Pencil,
  Trash2,
  Home,
  Clock,
  X,
  Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AvailabilityRule, OTAChannel, Property } from '@/types/revenue-manager';
import { useRoomInventory, useRoomTypes } from '@/hooks/use-room-inventory';
import { usePermissions } from '@/hooks/use-permissions';

const formatLastRun = (dateString: string | undefined): string => {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch {
    return 'Never';
  }
};

interface AvailabilityRulesTableProps {
  property: Property;
  rules: AvailabilityRule[];
  otaChannels: OTAChannel[];
  branchId?: string | number;
  basePrice?: string | number;
  onCreateRule: () => void;
  onEditRule: (rule: AvailabilityRule) => void;
  onDeleteRule: (ruleId: string) => void;
  onToggleRule: (ruleId: string, isActive: boolean) => void;
  onUpdateRule: (rule: AvailabilityRule) => void;
  onCloneRule: (rule: AvailabilityRule) => void;
  onExecuteRule: (ruleId: string) => void;
  onExport: () => void;
  onImport: () => void;
}

export function AvailabilityRulesTable({
  property,
  rules,
  otaChannels,
  branchId,
  basePrice,
  onCreateRule,
  onEditRule,
  onDeleteRule,
  onToggleRule,
  onUpdateRule,
  onCloneRule,
  onExecuteRule,
  onExport,
  onImport,
}: AvailabilityRulesTableProps) {
  const { canWrite, canDelete } = usePermissions();
  const { data: roomInventory } = useRoomInventory();
  const { data: roomTypesData } = useRoomTypes(branchId);
  const roomTypesCount = roomInventory?.data?.length || 0;
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const handleDeleteClick = (rule: AvailabilityRule) => {
    setRuleToDelete({ id: rule.id, name: rule.name });
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = () => {
    if (ruleToDelete) {
      onDeleteRule(ruleToDelete.id);
    }
    setDeleteDialogOpen(false);
    setRuleToDelete(null);
  };

  const getScope = (rule: AvailabilityRule) => {
    const roomCount = rule.roomTypes?.length || roomTypesCount;
    const channelCount = rule.otaChannels?.length || otaChannels.length;
    return { rooms: roomCount, channels: channelCount };
  };

  const handleRemoveVariant = (rule: AvailabilityRule, variantId: string) => {
    const updatedVariants = rule.selectedVariants?.filter(v => v.variantId !== variantId) || [];
    onUpdateRule({ ...rule, selectedVariants: updatedVariants });
  };

  const getRoomTypeName = (roomTypeId: string) => {
    const roomType = roomTypesData?.find(rt => String(rt.id) === roomTypeId);
    return roomType?.room_type || roomTypeId;
  };

  const getVariantInfo = (variantId: string) => {
    if (!roomTypesData) return null;
    
    for (const roomType of roomTypesData) {
      const variant = roomType.HotelRoomTypeVarientRelData?.find(
        (v: any) => String(v.id) === String(variantId)
      );
      if (variant) {
        return {
          roomTypeId: String(roomType.id),
          roomTypeName: roomType.room_type,
          variantCode: variant.variant_wise_code || `V${variantId}`,
          variantName: variant.VarientRoomTypeDataRel?.name || 'Unknown Variant'
        };
      }
    }
    return null;
  };

  const formatAction = (rule: AvailabilityRule) => {
    const actionLabels: Record<string, string> = {
      setStatus: 'Status',
      setMinStay: 'Min LOS',
      setMaxStay: 'Max LOS',
      setExactStay: 'Exact LOS',
      setAllotment: 'Allotment',
      adjustAllotment: 'Adjust Allotment',
    };
    const label = actionLabels[rule.actionType] || rule.actionType;
    const value = rule.actionType === 'adjustAllotment' && Number(rule.actionValue) > 0 
      ? `+${rule.actionValue}` 
      : rule.actionValue;
    return `${label}: ${value}`;
  };

  console.log('Rules in AvailabilityRulesTable:', rules);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Availability Rules</h2>
          <p className="text-sm text-muted-foreground">
            Control room availability, LOS restrictions, and booking windows
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" />
            Export Rules
          </Button>
          <Button variant="outline" size="sm" onClick={onImport} disabled={!canWrite}>
            <Upload className="mr-2 h-4 w-4" />
            Import Rules
          </Button>
          <Button size="sm" onClick={onCreateRule} disabled={!canWrite}>
            <Plus className="mr-2 h-4 w-4" />
            Create Rule
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">Priority</TableHead>
              <TableHead>Rule Name</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Last Run</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                      <CalendarX className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="mt-4 text-muted-foreground">No availability rules yet</p>
                    <p className="text-sm text-muted-foreground">
                      Create your first rule to control room availability
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => {
                const scope = getScope(rule);

                return (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {rule.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CalendarX className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <span className="font-medium">{rule.name}</span>
                          {rule.description && (
                            <p className="text-xs text-muted-foreground">{rule.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {formatAction(rule)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5 max-w-[200px]">
                        <Badge variant="outline" className="gap-1 w-fit">
                          <Home className="h-3 w-3" />
                          {scope.rooms}R · {scope.channels}C
                        </Badge>
                        {rule.selectedVariants && rule.selectedVariants.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {rule.selectedVariants.map((variant) => {
                              const variantInfo = getVariantInfo(variant.variantId);
                              const displayCode = variantInfo?.variantCode || variant.variantCode || `V${variant.variantId}`;
                              const displayName = variantInfo?.variantName || variant.variantName || 'Unknown';
                              const roomTypeName = variantInfo?.roomTypeName || getRoomTypeName(variant.roomTypeId);
                              
                              return (
                                <Badge
                                  key={variant.variantId}
                                  variant="secondary"
                                  className="text-[10px] gap-1 pr-1"
                                >
                                  <span className="truncate max-w-[80px]" title={`${roomTypeName} - ${displayName}`}>
                                    {displayCode}
                                  </span>
                                  {/* <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveVariant(rule, variant.variantId);
                                    }}
                                    className="hover:bg-muted rounded-full p-0.5"
                                  >
                                    <X className="h-3 w-3" />
                                  </button> */}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {rule.startDate} - {rule.endDate || 'Ongoing'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatLastRun(rule.lastRun)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {rule.processingFrequency === 'manual' && canWrite && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => onExecuteRule(rule.id)}
                                >
                                  <Play className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Run now</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <Switch
                          checked={rule.isActive}
                          onCheckedChange={() => onToggleRule(rule.id, rule.isActive)}
                          disabled={!canWrite}
                        />
                        {canWrite && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => onCloneRule(rule)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Clone rule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {canWrite && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => onEditRule(rule)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit rule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {canDelete && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteClick(rule)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete rule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Availability Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the rule "{ruleToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
